/**
 * tableOps.ts
 * Pure DOM utilities for manipulating HTML tables inside the rendered
 * template preview canvas. All functions operate on the live DOM —
 * no React state involved — so they work directly with the docx-preview
 * output. Structural changes are later captured by
 * `captureTemplateLayoutEdits` and persisted across re-renders.
 */

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Find the closest `<table>` ancestor of `el`, or `null`. */
export function findParentTable(el: HTMLElement): HTMLTableElement | null {
  return el.closest("table") as HTMLTableElement | null;
}

export interface CellPosition {
  table: HTMLTableElement;
  rowIndex: number;
  colIndex: number;
  cell: HTMLTableCellElement;
  /** First grid column the cell occupies (differs from colIndex when cells are merged). */
  gridIndex: number;
  /** How many grid columns the cell spans. */
  gridSpan: number;
}

/**
 * Given a `<td>` or `<th>`, return the table it belongs to, its row index
 * within the table, and where it sits on the column grid.
 */
export function findCellPosition(cell: HTMLElement): CellPosition | null {
  const td = cell.closest("td, th") as HTMLTableCellElement | null;
  if (!td) return null;
  const tr = td.parentElement as HTMLTableRowElement | null;
  if (!tr) return null;
  const table = findParentTable(td);
  if (!table) return null;

  const allRows = getAllRows(table);
  const rowIndex = allRows.indexOf(tr);
  const colIndex = Array.from(tr.cells).indexOf(td);

  let gridIndex = 0;
  for (const c of Array.from(tr.cells)) {
    if (c === td) break;
    gridIndex += c.colSpan || 1;
  }
  return { table, rowIndex, colIndex, cell: td, gridIndex, gridSpan: td.colSpan || 1 };
}

/** The cell covering grid column `g` in `row`, with its grid extent. */
function cellAtGrid(
  row: HTMLTableRowElement,
  g: number
): { cell: HTMLTableCellElement; start: number; span: number } | null {
  let cursor = 0;
  for (const cell of Array.from(row.cells)) {
    const span = cell.colSpan || 1;
    if (g >= cursor && g < cursor + span) return { cell, start: cursor, span };
    cursor += span;
  }
  return null;
}

/** Number of grid columns, counting merged cells by their span. */
export function gridColumnCount(table: HTMLTableElement): number {
  let max = 0;
  for (const row of getAllRows(table)) {
    let total = 0;
    for (const cell of Array.from(row.cells)) total += cell.colSpan || 1;
    if (total > max) max = total;
  }
  return max;
}

/* ------------------------------------------------------------------ */
/*  Column plan                                                       */
/* ------------------------------------------------------------------ */

/**
 * Which original grid column each current column came from ("n" = added by the
 * user). Merged cells make this impossible to reconstruct from the DOM alone,
 * so the plan is maintained on the table as columns are added and removed, and
 * later replayed against `<w:tblGrid>` / `<w:tc>` when saving.
 */
export const COL_PLAN_ATTR = "data-col-plan";

export function readColumnPlan(table: HTMLTableElement): Array<number | null> {
  const raw = table.getAttribute(COL_PLAN_ATTR);
  if (!raw) return [];
  return raw
    .split(",")
    .filter((s) => s.length > 0)
    .map((s) => (s === "n" ? null : Number(s)));
}

function writeColumnPlan(table: HTMLTableElement, plan: Array<number | null>): void {
  table.setAttribute(COL_PLAN_ATTR, plan.map((v) => (v === null ? "n" : String(v))).join(","));
}

/** Declare the current columns as the original ones (called after each render). */
export function resetColumnPlan(table: HTMLTableElement): void {
  writeColumnPlan(
    table,
    Array.from({ length: gridColumnCount(table) }, (_, i) => i)
  );
}

function updateColumnPlan(
  table: HTMLTableElement,
  mutate: (plan: Array<number | null>) => void
): void {
  const plan = readColumnPlan(table);
  if (plan.length === 0) return;
  mutate(plan);
  writeColumnPlan(table, plan);
}

/** Keep `<colgroup>` in step so column widths do not drift after an edit. */
function syncColGroup(table: HTMLTableElement, at: number, remove: boolean): void {
  const group = table.querySelector("colgroup");
  if (!group) return;
  const cols = Array.from(group.children);
  if (remove) {
    cols[at]?.remove();
    return;
  }
  const clone = (cols[at] ?? cols[cols.length - 1])?.cloneNode(true);
  if (!clone) return;
  if (cols[at]) group.insertBefore(clone, cols[at]);
  else group.appendChild(clone);
}

/** Collect all `<tr>` elements across thead/tbody/tfoot and bare rows. */
function getAllRows(table: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(table.querySelectorAll<HTMLTableRowElement>(":scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr"));
}

/** Count the maximum number of cells in any single row (effective column count). */
export function getColumnCount(table: HTMLTableElement): number {
  const rows = getAllRows(table);
  let max = 0;
  for (const row of rows) {
    if (row.cells.length > max) max = row.cells.length;
  }
  return max;
}

/** Mark the table as structurally modified so captureTemplateLayoutEdits picks it up. */
function markTableModified(table: HTMLTableElement): void {
  table.setAttribute("data-table-modified", "true");
}

/**
 * Clone a row, preserving structure (colspan, style, class) but clearing
 * text content. New cells get `contenteditable` when in edit mode.
 */
function cloneEmptyRow(sourceRow: HTMLTableRowElement): HTMLTableRowElement {
  const clone = sourceRow.cloneNode(true) as HTMLTableRowElement;
  // Clear text but keep child structure (spans etc.)
  Array.from(clone.cells).forEach((td) => {
    // Preserve any inner spans/divs but clear their text
    const deepLeaves = td.querySelectorAll("span, div, p");
    if (deepLeaves.length > 0) {
      deepLeaves.forEach((leaf) => {
        leaf.textContent = "";
      });
    } else {
      td.textContent = "";
    }
    // Remove any data attributes that flag loop rows or user edits
    td.removeAttribute("data-user-edited-text");
    td.removeAttribute("data-format-edited");
    td.removeAttribute("data-loop-row");
  });
  clone.removeAttribute("data-loop-row");
  clone.removeAttribute("data-user-edited-text");
  // Without this the clone would still claim the source row's position in the
  // DOCX, and saving would duplicate that row instead of adding an empty one.
  clone.removeAttribute("data-orig-row");
  return clone;
}

/** Create an empty cell matching the tag of sibling cells in the same column. */
function createEmptyCell(
  siblingTag: "TD" | "TH",
  siblingCell?: HTMLTableCellElement
): HTMLTableCellElement {
  const cell = document.createElement(siblingTag.toLowerCase()) as HTMLTableCellElement;
  // Copy basic styling from sibling if available
  if (siblingCell) {
    cell.style.cssText = siblingCell.style.cssText;
    // Don't copy content-related attributes
    cell.style.backgroundColor = "";
  }
  cell.style.padding = cell.style.padding || "5px 4px";
  cell.style.fontSize = cell.style.fontSize || "10.5px";
  cell.style.wordBreak = "break-word";
  cell.style.overflowWrap = "break-word";
  cell.style.whiteSpace = "normal";
  cell.style.boxSizing = "border-box";
  cell.style.maxWidth = "100%";
  return cell;
}

/* ------------------------------------------------------------------ */
/*  Row Operations                                                    */
/* ------------------------------------------------------------------ */

/**
 * Insert a new empty row above `rowIndex`.
 * Returns the newly inserted row, or `null` if the operation failed.
 */
export function insertRowAbove(
  table: HTMLTableElement,
  rowIndex: number
): HTMLTableRowElement | null {
  const rows = getAllRows(table);
  if (rowIndex < 0 || rowIndex >= rows.length) return null;
  const ref = rows[rowIndex];
  const newRow = cloneEmptyRow(ref);
  ref.parentElement!.insertBefore(newRow, ref);
  markTableModified(table);
  return newRow;
}

/**
 * Insert a new empty row below `rowIndex`.
 * Returns the newly inserted row, or `null` if the operation failed.
 */
export function insertRowBelow(
  table: HTMLTableElement,
  rowIndex: number
): HTMLTableRowElement | null {
  const rows = getAllRows(table);
  if (rowIndex < 0 || rowIndex >= rows.length) return null;
  const ref = rows[rowIndex];
  const newRow = cloneEmptyRow(ref);
  ref.parentElement!.insertBefore(newRow, ref.nextSibling);
  markTableModified(table);
  return newRow;
}

/**
 * Delete the row at `rowIndex`. Refuses if the table has only 1 row.
 * Returns `true` on success.
 */
export function deleteRow(table: HTMLTableElement, rowIndex: number): boolean {
  const rows = getAllRows(table);
  if (rows.length <= 1) return false;
  if (rowIndex < 0 || rowIndex >= rows.length) return false;
  rows[rowIndex].remove();
  markTableModified(table);
  return true;
}

/* ------------------------------------------------------------------ */
/*  Column Operations                                                 */
/* ------------------------------------------------------------------ */

/**
 * Insert a new empty column so that it becomes grid column `gridIndex`.
 * A cell that already straddles that position simply grows by one.
 * Returns `true` on success.
 */
export function insertColumnAt(table: HTMLTableElement, gridIndex: number): boolean {
  const rows = getAllRows(table);
  if (rows.length === 0) return false;
  const at = Math.max(0, Math.min(gridIndex, gridColumnCount(table)));

  for (const row of rows) {
    const hit = cellAtGrid(row, at);
    if (hit && hit.start < at) {
      // Inside a merged cell — widen it instead of breaking the merge.
      hit.cell.colSpan = hit.span + 1;
      continue;
    }
    const sibling = hit?.cell ?? row.cells[row.cells.length - 1];
    const tag = sibling?.tagName === "TH" ? "TH" : "TD";
    const newCell = createEmptyCell(tag as "TD" | "TH", sibling);
    if (hit) row.insertBefore(newCell, hit.cell);
    else row.appendChild(newCell);
  }

  syncColGroup(table, at, false);
  updateColumnPlan(table, (plan) => plan.splice(at, 0, null));
  markTableModified(table);
  return true;
}

/** Insert a column immediately before grid column `gridIndex`. */
export function insertColumnLeft(table: HTMLTableElement, gridIndex: number): boolean {
  return insertColumnAt(table, gridIndex);
}

/** Insert a column immediately after grid column `gridIndex`. */
export function insertColumnRight(table: HTMLTableElement, gridIndex: number): boolean {
  return insertColumnAt(table, gridIndex + 1);
}

/**
 * Delete grid column `gridIndex` from every row. Cells merged across it shrink
 * by one instead of disappearing. Refuses when only 1 column is left.
 */
export function deleteColumn(table: HTMLTableElement, gridIndex: number): boolean {
  const rows = getAllRows(table);
  if (rows.length === 0) return false;
  if (gridColumnCount(table) <= 1) return false;
  if (gridIndex < 0 || gridIndex >= gridColumnCount(table)) return false;

  for (const row of rows) {
    const hit = cellAtGrid(row, gridIndex);
    if (!hit) continue;
    if (hit.span > 1) hit.cell.colSpan = hit.span - 1;
    else hit.cell.remove();
  }

  syncColGroup(table, gridIndex, true);
  updateColumnPlan(table, (plan) => plan.splice(gridIndex, 1));
  markTableModified(table);
  return true;
}

/* ------------------------------------------------------------------ */
/*  Table-level Operations                                            */
/* ------------------------------------------------------------------ */

/**
 * Create a brand new `<table>` element with `rows` × `cols` empty cells.
 * Styled to match the docx-preview table appearance.
 */
export function createNewTable(rows: number, cols: number): HTMLTableElement {
  const table = document.createElement("table");
  table.style.width = "100%";
  table.style.maxWidth = "100%";
  table.style.tableLayout = "auto";
  table.style.borderCollapse = "collapse";
  table.style.boxSizing = "border-box";
  table.setAttribute("data-table-modified", "true");

  const tbody = document.createElement("tbody");
  for (let r = 0; r < rows; r++) {
    const tr = document.createElement("tr");
    for (let c = 0; c < cols; c++) {
      const td = document.createElement("td");
      td.style.border = "1px solid #d1d5db";
      td.style.padding = "5px 4px";
      td.style.fontSize = "10.5px";
      td.style.lineHeight = "1.25";
      td.style.wordBreak = "break-word";
      td.style.overflowWrap = "break-word";
      td.style.whiteSpace = "normal";
      td.style.boxSizing = "border-box";
      td.style.maxWidth = "100%";
      td.style.textAlign = "left";
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  return table;
}

/**
 * Build a table meant for data binding: a label row plus one row wrapped in
 * `{{#loop}} … {{/loop}}`, which the fill step repeats once per data row. Only
 * the column count is up to the user — the number of printed rows comes from
 * the data. Cells carry `{{Cột n}}` placeholders so the template stays valid
 * (an empty loop has no sample row) and can be renamed to real Excel columns.
 */
export function createDataTable(cols: number, loopName = "items"): HTMLTableElement {
  const count = Math.max(1, cols);
  const table = createNewTable(2, count);
  const [labelRow, dataRow] = Array.from(table.querySelectorAll("tr"));

  Array.from(labelRow.cells).forEach((cell, i) => {
    cell.textContent = `Cột ${i + 1}`;
    cell.style.fontWeight = "bold";
    cell.style.textAlign = "center";
  });

  Array.from(dataRow.cells).forEach((cell, i) => {
    const open = i === 0 ? `{{#${loopName}}}` : "";
    const close = i === count - 1 ? `{{/${loopName}}}` : "";
    cell.textContent = `${open}{{Cột ${i + 1}}}${close}`;
  });

  return table;
}

/** Drop `table` into the DOM just after the block holding `focusEl`. */
function placeTableAtCursor(
  container: HTMLElement,
  table: HTMLTableElement,
  focusEl?: HTMLElement | null
): HTMLTableElement {
  // Remember which existing table it follows, so the DOCX can place it there.
  const anchor = focusEl?.closest("table");
  table.setAttribute("data-new-table-after", anchor?.getAttribute("data-orig-table") ?? "");

  if (focusEl) {
    // Walk up to find the nearest top-level block (p, table, div) within a section
    let block: HTMLElement | null = focusEl;
    while (block && block !== container) {
      const parent: HTMLElement | null = block.parentElement;
      if (
        parent &&
        (parent.classList.contains("docx") ||
          parent.tagName === "SECTION" ||
          parent === container)
      ) {
        break;
      }
      block = parent;
    }
    if (block && block !== container) {
      block.parentElement!.insertBefore(table, block.nextSibling);
    } else {
      // Fallback: append to the first section
      const section = container.querySelector("section.docx") ?? container;
      section.appendChild(table);
    }
  } else {
    const section = container.querySelector("section.docx") ?? container;
    section.appendChild(table);
  }

  return table;
}

/**
 * Insert a plain `rows` × `cols` table after the block holding `focusEl`.
 */
export function insertNewTableAtCursor(
  container: HTMLElement,
  rows: number,
  cols: number,
  focusEl?: HTMLElement | null
): HTMLTableElement {
  return placeTableAtCursor(container, createNewTable(rows, cols), focusEl);
}

/**
 * Insert a data-bound table (label row + repeating row) after the block
 * holding `focusEl`.
 */
export function insertDataTableAtCursor(
  container: HTMLElement,
  cols: number,
  focusEl?: HTMLElement | null,
  loopName = "items"
): HTMLTableElement {
  return placeTableAtCursor(container, createDataTable(cols, loopName), focusEl);
}

/**
 * Remove an entire table from the DOM.
 */
export function deleteTable(table: HTMLTableElement): void {
  table.remove();
}

/* ------------------------------------------------------------------ */
/*  Style Operations                                                  */
/* ------------------------------------------------------------------ */

/**
 * Toggle borders on all cells in the table.
 * Returns the new border state: `true` if borders are now visible.
 */
export function toggleTableBorders(table: HTMLTableElement): boolean {
  const cells = table.querySelectorAll<HTMLTableCellElement>("td, th");
  if (cells.length === 0) return false;

  // Use a data attribute on the table to track border state reliably
  const hasBorder = table.getAttribute("data-borders-visible") !== "false";

  if (hasBorder) {
    // Turn borders off
    table.setAttribute("data-borders-visible", "false");
    cells.forEach((cell) => {
      cell.style.borderWidth = "0";
      cell.style.borderColor = "transparent";
      cell.setAttribute("data-format-edited", "true");
    });
  } else {
    // Turn borders on
    table.setAttribute("data-borders-visible", "true");
    cells.forEach((cell) => {
      cell.style.borderWidth = "1px";
      cell.style.borderStyle = "solid";
      cell.style.borderColor = "#d1d5db";
      cell.setAttribute("data-format-edited", "true");
    });
  }

  table.style.borderCollapse = "collapse";
  markTableModified(table);
  return !hasBorder;
}

/**
 * Set the background color of a single cell.
 */
export function setCellBackground(
  cell: HTMLTableCellElement,
  color: string
): void {
  cell.style.backgroundColor = color;
  cell.setAttribute("data-format-edited", "true");
  const table = findParentTable(cell);
  if (table) markTableModified(table);
}

/**
 * Get current row count and column count of a table.
 */
export function getTableDimensions(
  table: HTMLTableElement
): { rows: number; cols: number } {
  const rows = getAllRows(table);
  return { rows: rows.length, cols: gridColumnCount(table) };
}
